import { useTranslation } from "@/hooks/useTranslation";
import type { CreatorProfile, SocialAccount, SocialPlatform } from "@/hooks/useMarketplace";
import { CreatorCard } from "@/views/Marketplace/CreatorCard";

/**
 * Shape emitted by the `search_creators` tool (see
 * packages/api/src/agent/tools/creators.ts → summarize).
 *
 * Deliberately narrower than CreatorProfile: the tool flattens verification up
 * to the top level and splits follower totals so the model cannot blend a
 * verified count with a self-reported one.
 */
interface ToolCreator {
  handle: string;
  displayName: string;
  bio: string | null;
  country: string | null;
  city: string | null;
  niches: string[];
  contentFormats: string[];
  minRate: number | null;
  currency: string;
  acceptsGifting: boolean;
  avgRating: number | null;
  totalCollaborations: number;
  profileSource?: string;
  accounts: Array<{
    platform: string;
    handle: string;
    followers: number | null;
    engagementRate: number | null;
    verified: boolean;
    dataSource?: string;
  }>;
}

export interface SearchCreatorsOutput {
  totalMatching?: number;
  returned?: number;
  creators?: ToolCreator[];
}

/**
 * Map the tool's shape onto what CreatorCard renders.
 *
 * An adapter rather than a change to CreatorCard: the directory feeds that
 * component straight from GraphQL, and widening its props to accept two shapes
 * would make the card responsible for reconciling them.
 *
 * Fields the tool does not return (`url`, `avgViews`) become null — the card
 * treats them as absent, which they are. `id` falls back to the handle, which
 * is unique and is the only identifier the card actually links on.
 */
function toCardCreator(c: ToolCreator): CreatorProfile {
  const socialAccounts: SocialAccount[] = c.accounts.map((a, i) => ({
    id: `${c.handle}-${a.platform}-${i}`,
    platform: a.platform as SocialPlatform,
    handle: a.handle,
    url: null,
    followers: a.followers,
    engagementRate: a.engagementRate,
    avgViews: null,
    verified: a.verified,
    verifiedAt: null,
    // Fall back from the boolean when an older stored message predates
    // dataSource, so a replayed thread still renders a sensible badge.
    dataSource: (a.dataSource as SocialAccount["dataSource"]) ??
      (a.verified ? "OAUTH_VERIFIED" : "SELF_REPORTED"),
    lastSyncedAt: null,
  }));

  return {
    id: c.handle,
    handle: c.handle,
    displayName: c.displayName,
    bio: c.bio,
    avatarUrl: null,
    country: c.country ?? null,
    city: c.city,
    languages: [],
    niches: c.niches,
    contentFormats: c.contentFormats,
    minRate: c.minRate,
    baseCurrency: c.currency,
    acceptsGifting: c.acceptsGifting,
    portfolioUrls: [],
    isPublished: true,
    source: (c.profileSource as CreatorProfile["source"]) ?? "SELF_REGISTERED",
    claimedAt: null,
    derivedFields: [],
    avgRating: c.avgRating,
    completionRate: null,
    totalCollaborations: c.totalCollaborations,
    socialAccounts,
  };
}

/**
 * Creator search results, rendered as the real directory cards.
 *
 * These come from the tool's own database query, not from anything the model
 * wrote — the same reasoning that puts approval-card facts on the server in
 * routes/agent-approval.ts. It is why the verified badge can be trusted here
 * rather than depending on the model remembering to mention it.
 *
 * Cards link to /creadores/:handle, and that page re-fetches from the server.
 * Message parts are posted by the client, so a forged part could fake a preview
 * — but it cannot misrepresent the profile behind it, and it only ever fools
 * whoever forged it.
 */
export function CreatorResults({ output }: { output: SearchCreatorsOutput }) {
  const { t } = useTranslation();
  const creators = output.creators ?? [];

  if (creators.length === 0) {
    return (
      <p className="text-xs text-neutral-500">{t("assistant.results.noCreators")}</p>
    );
  }

  const total = output.totalMatching ?? creators.length;
  const hiddenCount = total - creators.length;

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-widest">
        {t("assistant.results.creatorCount", { count: total })}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {creators.map((c) => (
          <CreatorCard key={c.handle} creator={toCardCreator(c)} />
        ))}
      </div>

      {hiddenCount > 0 && (
        <p className="text-[11px] text-neutral-400">
          {t("assistant.results.andMore", { count: hiddenCount })}
        </p>
      )}
    </div>
  );
}
